import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import axios from 'axios';
import '@/App.css';
import LoginFirebase from '@/pages/LoginFirebase';
import RegisterFirebase from '@/pages/RegisterFirebase';
import ForgotPasswordFirebase from '@/pages/ForgotPasswordFirebase';
import Dashboard from '@/pages/Dashboard';
import Patients from '@/pages/Patients';
import PatientDetail from '@/pages/PatientDetail';
import Appointments from '@/pages/Appointments';
import Infusions from '@/pages/Infusions';
import InfusionMonitorPage from '@/pages/InfusionMonitorPage';
import PatientView from '@/pages/PatientView';
import SettingsPage from '@/pages/SettingsPage';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';

export const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

// Configure axios to include auth token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get fresh token
        const token = await firebaseUser.getIdToken();
        localStorage.setItem('token', token);
        
        setUser({
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email,
          uid: firebaseUser.uid
        });
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('token');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Route publique pour la vue patient (accessible sans auth) */}
        <Route path="/patient-view/:infusionId" element={<PatientView />} />
        
        <Route
          path="/login"
          element={
            user ? <Navigate to="/" replace /> : <LoginFirebase onLogin={handleLogin} />
          }
        />
        <Route
          path="/register"
          element={
            user ? <Navigate to="/" replace /> : <RegisterFirebase onLogin={handleLogin} />
          }
        />
        <Route
          path="/forgot-password"
          element={
            user ? <Navigate to="/" replace /> : <ForgotPasswordFirebase />
          }
        />
        <Route
          path="/*"
          element={
            user ? (
              <Layout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Dashboard user={user} />} />
                  <Route path="/patients" element={<Patients />} />
                  <Route path="/patients/:id" element={<PatientDetail user={user} />} />
                  <Route path="/appointments" element={<Appointments />} />
                  <Route path="/infusions" element={<Infusions />} />
                  <Route path="/infusions/monitor/:id" element={<InfusionMonitorPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
